import { FC, ReactElement, useEffect, useState } from 'react';
import { getCurrentLoB } from '../../lib/utils/utils';
import { FieldErrors, useForm } from 'react-hook-form';
import { clickAnalytics, formErrorAnalytics, pageLoadAnalytics } from '../../lib/utils/analytics';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input, Link, Textarea } from '@digital-pulse/react';
import Form from '../../components/form/form';
import PageAlerts from '../../components/alert/pageAlerts';
import AppLayout from '../../components/appLayout/appLayout';
import styles from './feedback.module.scss';
import { useRouter } from 'next/router';
import { FEEDBACK_ERROR_TITLES, resolveConsText } from '../../lib/constants/feedback';
import { enforceNumber, formatToPhone } from '../../lib/utils/phoneNumberUtils';
import { renderTemplate } from '../../lib/utils/templateUtils';
import { feedbackSchema } from '../../lib/dataSchemas/feedbackSchema';
import useAlertProvider from '@/components/alert/useAlertProvider';
import { CVSData } from '@/lib/cvsdata/cvsdata';
import { ERROR_CONS } from '@/lib/constants/error';

const feedbackFormSchema = feedbackSchema();
type FormData = yup.InferType<typeof feedbackFormSchema>;
const reValidateMode: 'onSubmit' | undefined = 'onSubmit';

const Feedback: FC = (): ReactElement | null => {
  const { setAlerts } = useAlertProvider();
  const { formState, handleSubmit, register, reset } = useForm<FormData>({
    context: {
      feedbackFormSchema
    },
    resolver: async (data, context, options) => {
      return yupResolver(feedbackFormSchema)(data, context, options);
    },
    reValidateMode
  });
  const { query, push } = useRouter();
  const formName = 'feedback-form';
  const [feedback, updateFeedback] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [lob, setLob] = useState('cvs');

  useEffect(() => {
    setLob(getCurrentLoB());
    pageLoadAnalytics('generate_new_feedback:your_feedback');
  }, []);

  const cons = resolveConsText(lob);
  const { onChange: phoneOnChange, onBlur: phoneOnBlur, ...phoneRegister } = register('phoneNumber');

  const onInvalidSubmit = (errors: FieldErrors<FormData>) => {
    formErrorAnalytics(errors, 'your_feedback', 'account:generate_new_feedback:your_feedback:error');
  };

  const submitData = async (fieldValues: FormData) => {
    setAlerts([]);
    clickAnalytics('account:generate_new_feedback:your_feedback:submit');
    const res = await CVSData.submitSurvey({
      xid: query?.xid?.toString() || 'NO_XID',
      emailConsent: 'Y',
      appVersion: '',
      ...fieldValues,
    })
    if(res?.data?.updateAppSurvey?.statusCode === '0000'){
      updateFeedback('');
      setPhoneNumber('');
      reset({
        firstName: '',
        emailAccount: '',
        phoneNumber: '',
        surveyComment: '',
      });
      push({
        pathname: '/success'
      })
    } else if(res?.fault?.type === 'entp.fdbk.errortypes.DUPLICATE_XID'){
      setAlerts([
        {
          title: ERROR_CONS.expired_xid.title,
          message: ERROR_CONS.expired_xid.description
        }
      ])
    } else if(res?.fault?.type === 'entp.fdbk.errortypes.INVALID_XID'){
      setAlerts([
        {
          title: ERROR_CONS.invalid_xid.title,
          message: ERROR_CONS.invalid_xid.description
        }
      ])
    } else {
      setAlerts([
      {
        title: ERROR_CONS.tech_error.title,
        message: ERROR_CONS.tech_error.description,
      }
    ]);
  };
}

  return (
    <AppLayout pageTitle={'Gather Feedback'} pageName="Feedback Page">
      <>
        <div className={styles.phoneDOB__container}>
          <Form
            formState={formState}
            errorTitle={FEEDBACK_ERROR_TITLES.DEFAULT}
            onSubmit={handleSubmit(submitData, onInvalidSubmit)}
            data-test-id={formName}
          >
            <h1 className={styles.title}>{cons.title}</h1>
            <p className={styles.description}>
              {renderTemplate(cons.description, {
                PRIVACY_POLICY_LINK: <Link isNewTab href={cons.privacyPolicyUrl}>{cons.privacyPolicyLabel}</Link>
              })}
            </p>
            <PageAlerts />
            <div className={styles.phoneDOB__form}>
              <h3>{cons.contact_header}</h3>
              <Input
                className={styles.feedback_input}
                label={cons.firstName}
                {...register('firstName')}
                error={formState.errors.firstName?.message}
              />
              <Input
                className={styles.feedback_input}
                label={cons.email}
                {...register('emailAccount')}
                error={formState.errors.emailAccount?.message}
              />
              <Input
                className={styles.feedback_input}
                // @ts-expect-error - need to update type definition for label to accept ReactNode
                label={<>{cons.phoneNumber} <span className={styles.optionalLabel}>{cons.optionalLabel}</span></>}
                aria-label={`${cons.phoneNumber} ${cons.optionalLabel}`}
                type="tel"
                {...phoneRegister}
                onKeyDown={enforceNumber}
                onKeyUp={(e) => { formatToPhone(e); setPhoneNumber(e.currentTarget.value); }}
                onChange={(e) => { formatToPhone(e); setPhoneNumber(e.target.value); phoneOnChange(e); }}
                onBlur={(e) => { formatToPhone(e); setPhoneNumber(e.target.value); phoneOnBlur(e); }}
                value={phoneNumber}
                error={formState.errors.phoneNumber?.message}
              />
              <Textarea
                {...register('surveyComment')}
                className={styles.textArea}
                // @ts-expect-error - need to update type definition for label to accept ReactNode
                label={<>{cons.feedback} <span className={styles.optionalLabel}>{cons.optionalLabel}</span></>}
                aria-label={`${cons.feedback} ${cons.optionalLabel}`}
                helper={cons.feedback_helper}
                onChange={(e) => updateFeedback(e.target.value)}
                value={feedback}
                error={formState.errors.surveyComment?.message}
              ></Textarea>
              <p className={styles.counter} aria-label="feedback character count">
                {feedback.length}/1000
              </p>
            </div>
          </Form>
        </div>
      </>
    </AppLayout>
  );
};

export default Feedback;
